import { useEffect } from "react";
import { Link, Outlet, useLocation, useMatch, useNavigate, useSearchParams } from "react-router-dom";

import { logout, useHealth, useIncomingHandovers, useMe, useMeCache } from "@/api";
import { CaseHeadSlots } from "@/components/CaseHeadSlots";
import { FolderIcon, HomeIcon } from "@/components/icons";
import { ToastHost, ToastProvider } from "@/components/Toast";
import { K60Logo } from "@/brand/K60Logo";
import { usePageSide } from "@/lib/motion";
import { useArrivalAnnouncement } from "@/lib/useArrivalAnnouncement";
import { useSession } from "@/store/session";

import { AccountMenu } from "./AccountMenu";
import styles from "./AppShell.module.css";
import { CaseFrame } from "./CaseFrame";
import { HelpControl } from "./Help";
import { pageWidth } from "./pageWidth";
import { ThemeToggle } from "./ThemeToggle";

/**
 * The API's health, said only when it is not (UX gate round 2, #9, ruling
 * b): a dot and „API" in every masthead was a technician's reading nobody
 * in sales acts on. While the server answers, nothing shows. When it does
 * not — no answer at all, or an answer that is not a health report — a bar
 * the width of the window stands under the masthead's four-colour bar
 * (round-3 screen check, R3-11): in the masthead it squeezed the name to
 * „DENIS MUS…" on a phone and stood after „Abmelden" on a wide screen. One
 * line, what is wrong and what to do, and the status line that stands from
 * the first render reads it out once. Ink, not a status colour: it is about
 * the app, not about data, and red under the brand's bar would read as part
 * of it (CI §2.1); the words carry the state.
 */
function HealthNotice() {
  const { data, isPending, isError } = useHealth();
  const down = !isPending && (isError || data === null || data === undefined);
  return (
    <div role="status" id="server-status" className={down ? styles.healthBar : "visually-hidden"}>
      {down ? (
        <>
          <span className={styles.healthDot} aria-hidden="true" />
          <span>
            <strong className={styles.healthLead}>Server nicht erreichbar</strong> — versuch es gleich noch einmal.
          </span>
        </>
      ) : null}
    </div>
  );
}

/**
 * The number of handovers waiting for me (design §8.3), at „Start" in the
 * masthead — brand red, because the masthead is chrome (CI §2.1). Read out as
 * a sentence; the bare digit is hidden from screen readers so it is not
 * announced twice.
 *
 * Laid over the place's upper right, not beside its word (UX-Gutachten 2,
 * B-5): beside it, its box was kept even at zero (fix round 1, M3: the first
 * arrival would have pushed „Vorgänge" 28 px right), and the places stood
 * 60 px apart where the product line and „Start" stood 25. Positioned, it
 * takes no room — the first arrival still moves nothing — so it is drawn
 * only when something waits.
 */
function WaitingBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className={styles.badge}>
      <span className="tabular" aria-hidden="true">
        {count}
      </span>
      <span className="visually-hidden">
        {count === 1 ? "1 Übergabe wartet auf dich" : `${count} Übergaben warten auf dich`}
      </span>
    </span>
  );
}

/**
 * The lit place's mark: a lit segment of the masthead's four-colour bar under
 * its word (design 2026-10-24 §4.1; UX-Gutachten 2, B-5 — standing 4 px over
 * the bar it read as a second red line over the bar's red quarter). An
 * element of its own, not a border of the word, so a page turn can carry it
 * from one place to the other under its own view-transition name
 * (`ort-marke`, §7) while the word stays where it is. Only the lit place
 * draws one — a view-transition name may stand on one element of the page at
 * a time.
 */
function PlaceMark() {
  return <span className={styles.placeMark} aria-hidden="true" />;
}

function Shell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const caseMatch = useMatch("/vorgang/:caseId/*");
  const recordMatch = useMatch("/vorgang/:caseId");
  const registerMatch = useMatch("/vorgaenge");
  const outlineMatch = useMatch("/umriss");
  const [search] = useSearchParams();
  // `/umriss?case=<id>` draws for that Vorgang (round-2 screen check, R2-1) —
  // Stand 1, the reading's outline put right, or „Form ändern …": a page of
  // the Vorgang, framed like the rest of them. Without `case` it is a *new*
  // Vorgang's drawing, which belongs to Start, where it was begun.
  const outlineCaseId = outlineMatch !== null ? search.get("case") || null : null;
  const frameCaseId = caseMatch?.params.caseId ?? outlineCaseId;
  // Which place a page belongs to (design 2026-10-22 §4): Start for the start
  // page and the free drawing, the register for every page of a Vorgang;
  // example pages, `/_muster` and a dead end belong to neither. Links with
  // their own `aria-current`, not `NavLink`s: a `NavLink` matches the path
  // alone, and `/umriss` is Start's without `case` and the register's with it.
  const startActive = pathname === "/" || (outlineMatch !== null && outlineCaseId === null);
  const registerActive = registerMatch !== null || caseMatch !== null || outlineCaseId !== null;
  const pageSide = usePageSide();
  // Wider than the content width where the page must not stop at it — the
  // frozen review and the two drawing surfaces (`pageWidth`) —, said on the
  // page and on the Vorgang's head above it, so they keep one right edge.
  const breit = pageWidth(pathname);
  const { data: me } = useMe();
  const meCache = useMeCache();
  const setReviewer = useSession((state) => state.setReviewer);
  const incoming = useIncomingHandovers();
  const waiting = Array.isArray(incoming.data) ? incoming.data.length : null;
  const arrival = useArrivalAnnouncement(waiting);

  // RequireSession only ever renders this shell once `me` is a real user
  // (§ RequireSession.tsx), so this runs with a name to give — the guard
  // upstream is what keeps `me` from being null here.
  useEffect(() => {
    if (me) setReviewer(me.displayName);
  }, [me, setReviewer]);

  async function handleLogout() {
    try {
      await logout();
    } finally {
      // Written synchronously, even if the server call failed — a session
      // that no longer answers is not one to keep pretending to hold, and
      // whatever mounts at /anmelden next (nothing does today, but see
      // auth.ts's useMeCache doc) should not find the old user still cached.
      meCache.clear();
      navigate("/anmelden");
    }
  }

  return (
    <div className={styles.shell}>
      {/* First Tab stop, hidden until it has focus (M6): the masthead is
          several stops before the content on every screen, and a Vorgang's
          page adds its tabs. */}
      <a href="#main" className={styles.skipLink}>
        Zum Inhalt
      </a>

      {/* The masthead carries the two places of the app (design 2026-10-22
          §4, Entscheid Denis 22.10.2026): where a session starts, and every
          Vorgang of mine. There is no rail any more — its steps were the
          Vorgang's, and they stand now as the tabs of the Vorgang's frame
          (§5), on the Vorgang's pages and nowhere else, so nothing here is
          ever locked or greyed out (§3). Words only, no hint lines; the
          place a page belongs to is marked in brand red on the masthead's
          lower edge, because the masthead is chrome (CI §2.1).

          Since the decision of 24.10.2026 (design 2026-10-24 §4.1) it is
          steel: anthracite in both modes, K60's original logo in its colours
          on a white nameplate at the window's edge — and the plate leads
          home, as the logo does on any website (addendum Denis, 24.10.).
          Named „Startseite“ so it is not a second „Start“ beside the place
          of that name. The product line stands beside the plate, on the
          anthracite.

          `data-masthead`: no menu or other floating layer of the page ever
          lies over it (`floatingCeiling`, UX-Gutachten 2, B-1). */}
      <header className={styles.header} data-masthead="">
        <Link to="/" className={styles.brand} aria-label="Startseite">
          <K60Logo height={40} />
        </Link>
        <span className={styles.product}>Werkbank Hausprojekte</span>

        {/* Present before its text: a new handover is said once (F-16). */}
        <p role="status" className="visually-hidden">
          {arrival}
        </p>

        <nav aria-label="Hauptnavigation" className={styles.places}>
          <Link
            to="/"
            aria-current={startActive ? "page" : undefined}
            className={`${styles.place} ${startActive ? styles.placeActive : ""}`}
          >
            <span className={styles.placeName}>
              <HomeIcon size={14} />
              Start
              {startActive ? <PlaceMark /> : null}
              <WaitingBadge count={waiting ?? 0} />
            </span>
          </Link>
          <Link
            to="/vorgaenge"
            aria-current={registerActive ? "page" : undefined}
            className={`${styles.place} ${registerActive ? styles.placeActive : ""}`}
          >
            <span className={styles.placeName}>
              <FolderIcon size={14} />
              Vorgänge
              {registerActive ? <PlaceMark /> : null}
            </span>
          </Link>
        </nav>

        {/* The masthead's right end in one type rule (UX-Gutachten 2, B-5):
            „?" for the keys of the screen, with a dot while something new
            waits (Welle 1, idea 16; `HelpControl`), the switch for light or
            dark (design 2026-10-24 §5.1), and the account — the name and
            „Abmelden" behind the initials (`AccountMenu`). */}
        <div className={styles.headerMeta}>
          <HelpControl />
          <ThemeToggle />
          {me ? <AccountMenu name={me.displayName} onLogout={() => void handleLogout()} /> : null}
        </div>
      </header>

      <HealthNotice />

      <div className={styles.body}>
        {/* tabIndex=-1: focusable as the skip link's target, never in the
            Tab order on its own (M6). */}
        <main id="main" className={styles.main} tabIndex={-1}>
          {/* Every page of a Vorgang stands in its frame — the Vorgang's one
              head, with its tabs (design 2026-10-22 §5; Task 9, B-4). Keyed
              by the Vorgang: moving to another one draws its frame afresh.
              The head's places that only the page can fill (the record's
              way on, its „Mehr …", its stage path) are handed to the page
              through `CaseHeadSlots` — rendered around every page, framed or
              not, so a page keeps its place in the tree. */}
          <CaseHeadSlots framed={frameCaseId !== null}>
            {frameCaseId !== null ? (
              <CaseFrame key={frameCaseId} caseId={frameCaseId} onRecord={recordMatch !== null} breit={breit} />
            ) : null}
            {/* The page itself, under a name of its own for the page turns
                (design 2026-10-24 §7): it rises in, or turns in reading
                direction from a tab, while the frame above it stands — one of
                two names, the other after every navigation (`usePageSide`). */}
            <div className={styles.page} data-seite={pageSide} data-breit={breit}>
              <Outlet />
            </div>
          </CaseHeadSlots>
        </main>
      </div>
    </div>
  );
}

/**
 * The shell, with the toasts of every screen under it (Welle 1, idea 23):
 * one provider around the page, so a toast shown on one page still stands on
 * the next — a Vorgang made on `/umriss` is said on its board
 * (`/vorgang/:id/planung`), where the person lands. The host is drawn once,
 * into `<body>` (BW-9): one polite live region, there from the first render,
 * so the first toast is heard.
 */
export function AppShell() {
  return (
    <ToastProvider>
      <Shell />
      <ToastHost />
    </ToastProvider>
  );
}
